import { Job, JobSkill } from '../models/job.model';

export type JobReadinessKey = 'basics' | 'workSetup' | 'description' | 'skills';

export interface JobReadinessItem {
  key: JobReadinessKey;
  label: string;
  complete: boolean;
  detail: string;
}

export interface JobValidationResult {
  errors: string[];
  messages: Record<string, string>;
}

const hasDescription = (description: string) => {
  const text = description
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > 0;
};

export const getJobReadiness = (
  job: Job,
  skills: Pick<JobSkill, 'skillName'>[]
) => {
  const requirements = [
    Boolean(job.name?.trim()),
    Boolean(job.category?.trim()),
    Boolean(job.subCategory?.trim()),
    Boolean(job.clientId?.trim()),
    Boolean(job.location?.trim()),
    Boolean(job.region || job.countries?.length),
    hasDescription(job.description || ''),
    skills.length > 0,
  ];

  const completedRequirements = requirements.filter(Boolean).length;
  const score = Math.round((completedRequirements / requirements.length) * 100);

  const items: JobReadinessItem[] = [
    {
      key: 'basics',
      label: 'Basic information',
      complete: requirements.slice(0, 4).every(Boolean),
      detail: 'Title, client and job classification',
    },
    {
      key: 'workSetup',
      label: 'Work setup',
      complete: requirements.slice(4, 6).every(Boolean),
      detail: 'Workplace and hiring region',
    },
    {
      key: 'description',
      label: 'Job description',
      complete: requirements[6],
      detail: 'Role overview and responsibilities',
    },
    {
      key: 'skills',
      label: 'Matching skills',
      complete: requirements[7],
      detail: 'At least one required skill',
    },
  ];

  return {
    score,
    completedRequirements,
    totalRequirements: requirements.length,
    items,
    isReady: score === 100,
  };
};

export const formatJobCompensation = (job: Job) => {
  const symbol = job.currency === 'CRC_COLON' ? '₡' : '$';
  const format = (value: string) =>
    Number(value).toLocaleString(
      job.currency === 'CRC_COLON' ? 'es-CR' : 'en-US'
    );

  if (job.minSalary && job.maxSalary) {
    return `${symbol}${format(job.minSalary)} – ${symbol}${format(job.maxSalary)}`;
  }

  if (job.minSalary) {
    return `From ${symbol}${format(job.minSalary)}`;
  }

  if (job.maxSalary) {
    return `Up to ${symbol}${format(job.maxSalary)}`;
  }

  return 'Not specified';
};

export const formatJobRegion = (job: Job) => {
  if (job.region) {
    return job.region;
  }

  if (job.countries?.length) {
    const [firstCountry, ...remainingCountries] = job.countries;
    return remainingCountries.length
      ? `${firstCountry} +${remainingCountries.length}`
      : firstCountry;
  }

  return 'Not set';
};

export const validateJob = (job: Job): JobValidationResult => {
  const errors: string[] = [];
  const messages: Record<string, string> = {};

  const requireField = (field: string, present: boolean, message: string) => {
    if (!present) {
      errors.push(field);
      messages[field] = message;
    }
  };

  requireField('name', Boolean(job.name?.trim()), 'You must enter the name.');
  requireField(
    'category',
    Boolean(job.category?.trim()),
    'You must select the category.'
  );
  requireField(
    'subCategory',
    Boolean(job.subCategory?.trim()),
    'You must select the subcategory.'
  );
  requireField(
    'clientId',
    Boolean(job.clientId?.trim()),
    'You must select the client.'
  );
  requireField(
    'countries',
    Boolean(job.region || job.countries?.length),
    'You must select at least one country and/or LATAM region.'
  );
  requireField(
    'location',
    Boolean(job.location?.trim()),
    'You must select the location.'
  );
  requireField(
    'description',
    hasDescription(job.description || ''),
    'You must enter the description.'
  );
  requireField(
    'skills',
    Boolean(job.skills?.length),
    'You must add at least one skill.'
  );

  return { errors, messages };
};
